import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {DemoAccountMutationError} from 'constants/errors';
import {ReactNode, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import SignupPromptModal from 'ui/@_components/signup-prompt-modal';
import useDemoAccount from 'ui/@hooks/use-demo-account';
import useDemoAccountDetails from 'ui/@hooks/use-demo-account-details';
const FIVE_MINS = 30_00_00; // 5mins

interface Props {
  children: ReactNode;
}

const InstillQueryClientProvider = ({children}: Props) => {
  const [isSignupPromptModalOpen, setIsSignupPromptModalOpen] = useState(false);

  const {isDemoAccount} = useDemoAccount();

  const {mutationToast} = useDemoAccountDetails();

  const isQuotaExceededError = (error: unknown): boolean => {
    if (!error) return false;

    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      return true;
    }

    if (typeof error === 'object') {
      const err = error as {name?: string; message?: string};
      return (
        err.name === 'QuotaExceededError' ||
        (typeof err.message === 'string' &&
          (err.message.includes('quota') || err.message.includes('storage')))
      );
    }

    return false;
  };

  const handleQuotaExceededError = (queryClient: QueryClient) => {
    const queries = queryClient.getQueryCache().getAll();

    const removeCount = Math.ceil(queries.length * 0.3);

    if (removeCount > 0) {
      const oldestQueries = queries
        .sort(
          (a, b) => (a.state.dataUpdatedAt || 0) - (b.state.dataUpdatedAt || 0)
        )
        .slice(0, removeCount);

      oldestQueries.forEach((query) => {
        queryClient.removeQueries(query.queryKey);
      });
    }
  };

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          cacheTime: FIVE_MINS,
          staleTime: FIVE_MINS,
          onError: (error: unknown) => {
            if (isQuotaExceededError(error)) {
              handleQuotaExceededError(queryClient);
            } else {
              console.error('Query error:', error);
            }
          },
        },
        mutations: {
          retry: 3,
          retryDelay: 2000,
          onMutate: () => {
            if (isDemoAccount) {
              if (mutationToast.show) {
                toast.error(mutationToast.text);
              } else {
                setIsSignupPromptModalOpen(true);
              }
              throw new DemoAccountMutationError(
                'Create/ Update/ Delete operations are disabled in demo mode.'
              );
            }
          },
          onError: (error: unknown) => {
            if (isQuotaExceededError(error)) {
              handleQuotaExceededError(queryClient);
            }
          },
        },
      },
    });
  }, [isDemoAccount, mutationToast.show, mutationToast.text]);

  return (
    <>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>

      <SignupPromptModal
        isOpen={isSignupPromptModalOpen}
        onClose={() => setIsSignupPromptModalOpen(false)}
      />
    </>
  );
};

export default InstillQueryClientProvider;
